import { GameScene } from '@Core/GameScene/GameScene';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { TPoint } from '@GFTypes/Point';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { VIRTUAL_SIZES } from '@Managers/ScaleManager/ScaleManager';
import { Graphics, Point, Rectangle, Sprite, Texture } from 'pixi.js';

export interface IGeneratedWinlineConfig {
    color: string;
    colors: { [K in number]: string };
    width: number;
    alpha: number;
    borderOffset: number;
    duration: number;
}

export class GeneratedWinlines {
    private readonly gameStore: GameStore;
    private generatedWinline: Sprite | undefined;
    private currentTexture?: Texture;
    private textureCounter = 0;

    constructor(public readonly scene: GameScene) {
        this.scene = scene;
        this.gameStore = this.scene.game.gameStore;
        Logger.log(LoggerLevel.Constructor, { class: GeneratedWinlines.name, source: LoggerSource.Game, message: 'Generated Winlines constructor' });
    }

    private getGameScaleFactor(): number {
        const scaleX = this.scene.screen.width / VIRTUAL_SIZES.LANDSCAPE.x;
        const scaleY = this.scene.screen.height / VIRTUAL_SIZES.LANDSCAPE.y;
        return Math.min(scaleX, scaleY);
    }

    private getDevicePixelRatio(): number {
        return this.scene.game.renderer.renderer.resolution;
    }

    public generateWinline(points: TPoint[], winlineId: number): void {
        if (points.length < 2) return;

        this.destroyWinline();

        const { color, colors, width, alpha } = this.gameStore.get(EGameStoreKeys.WinConfiguration).generatedWinlineConfig;
        const winlinePresentationId = winlineId + 1;
        const winlineColor = colors?.[winlinePresentationId] ? colors[winlinePresentationId] : color;

        const gameScale = this.getGameScaleFactor();
        const dpr = this.getDevicePixelRatio();
        const scaledWidth = Math.max(1, width * gameScale);

        const minX = Math.min(...points.map((p) => p.x));
        const minY = Math.min(...points.map((p) => p.y));
        const maxX = Math.max(...points.map((p) => p.x));
        const maxY = Math.max(...points.map((p) => p.y));
        const pad = scaledWidth;

        const tempGraphics = new Graphics();
        tempGraphics.setStrokeStyle({
            width: scaledWidth,
            color: Number.parseInt(winlineColor, 16),
            alpha
        });
        tempGraphics.beginPath();
        tempGraphics.moveTo(points[0].x - minX + pad, points[0].y - minY + pad);

        for (const point of points) {
            tempGraphics.lineTo(point.x - minX + pad, point.y - minY + pad);
        }

        tempGraphics.stroke();

        const frameWidth = Math.ceil(maxX - minX + pad * 2);
        const frameHeight = Math.ceil(maxY - minY + pad * 2);
        const textureResolution = Math.max(1, dpr);

        const texture = this.scene.game.renderer.renderer.generateTexture({
            target: tempGraphics,
            resolution: textureResolution,
            frame: new Rectangle(0, 0, frameWidth, frameHeight),
            antialias: true
        });

        tempGraphics.destroy();

        const image = new Sprite({
            texture,
            position: { x: minX - pad, y: minY - pad },
            anchor: { x: 0, y: 0 },
            zIndex: ELayeringOrder.WINLINES,
            roundPixels: false
        });

        this.generatedWinline = image;
        this.scene.stage.addChild(this.generatedWinline);
        this.generatedWinline.zIndex = ELayeringOrder.WINLINES;
        this.currentTexture = texture;
    }

    public showWinline(allWinlinePositions: number[], winlineId: number): void {
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        const points: TPoint[] = [];
        const visSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const { borderOffset } = this.gameStore.get(EGameStoreKeys.WinConfiguration).generatedWinlineConfig;
        const { baseWidth } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);

        visSymbols.forEach((reel) => {
            reel.forEach((symbol) => {
                if (allWinlinePositions[symbol.reelIndex] === symbol.visibleSymbolIndex) {
                    const position = symbol.toGlobal(new Point(0, 0));

                    if (symbol.reelIndex === 0) {
                        const firstPos = symbol.toGlobal(new Point(-(baseWidth + borderOffset) / 2, 0));

                        points.push({ x: firstPos.x, y: firstPos.y });
                    }

                    points.push({ x: position.x, y: position.y });

                    if (symbol.reelIndex === numberOfReels - 1) {
                        const lastPos = symbol.toGlobal(new Point((baseWidth + borderOffset) / 2, 0));

                        points.push({ x: lastPos.x, y: lastPos.y });
                    }
                }
            });
        });

        this.generateWinline(points, winlineId);
    }

    destroyWinline(): void {
        this.generatedWinline?.destroy();
        this.generatedWinline = undefined;

        this.currentTexture?.destroy(true);
        this.currentTexture = undefined;

        this.textureCounter = 0;
    }
}
